import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getTxDetail } from "@/lib/api";
import { TX } from "@/constants/testIds";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Hash, User, Blocks, Fuel, CheckCircle, XCircle, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { FADE_UP } from "@/lib/motion";

function formatFMI(weiStr) {
  return (Number(weiStr) / 1e18).toFixed(6);
}

function formatGwei(weiStr) {
  return (Number(weiStr) / 1e9).toFixed(2);
}

const amt = (a) => `${Number(a.amount).toLocaleString(undefined, { maximumFractionDigits: 6 })} ${a.token}`;
const TRADE_LABEL = { buy: "Buy FMI", sell: "Sell FMI", swap: "Swap" };

const tradeRows = (t) => [
  { label: "Trade", value: `${TRADE_LABEL[t.kind]} · ${t.paid.token} → ${t.received.token}`, testId: "tx-trade-type" },
  { label: `${t.paid.token} Paid`, value: amt(t.paid), testId: "tx-trade-paid" },
  { label: `${t.received.token} Received`, value: amt(t.received), testId: "tx-trade-received" },
  { label: t.kind === "swap" ? "Pool Fee (0.2%)" : "Fee (0.15%)", value: amt(t.fee), testId: "tx-trade-fee" },
  ...(t.tax ? [{ label: "Tax", value: amt(t.tax), testId: "tx-trade-tax" }] : []),
  { label: "Receipt", value: <Link to={`/receipt/${t.hash}`} className="table-link">Open shareable receipt →</Link>, testId: "tx-trade-receipt-link" },
];

const STATUS_STYLE = {
  Success: { icon: CheckCircle, iconClass: "text-green-500", badge: "bg-green-500/10 text-green-400 border-green-500/30" },
  Pending: { icon: Clock, iconClass: "text-yellow-500", badge: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" },
  Failed: { icon: XCircle, iconClass: "text-red-500", badge: "bg-red-500/10 text-red-400 border-red-500/30" },
};

const statusLabel = (tx) => {
  if (tx.pending) return "Pending";
  return tx.status === 1 ? "Success" : "Failed";
};

const recipientRow = (tx) => {
  if (tx.to) return { label: tx.toIsContract ? "To (Contract)" : "To", value: tx.to, icon: User, mono: true, link: `/address/${tx.to}`, testId: TX.to };
  const created = tx.contractAddress;
  return { label: "Contract Created", value: created || "Contract Creation", icon: User, mono: true, link: created ? `/address/${created}` : null, testId: TX.to };
};

const valueRows = (tx) => (tx.trade ? tradeRows({ ...tx.trade, hash: tx.hash }) : [{ label: "Value", value: `${formatFMI(tx.value)} FMI`, testId: TX.value }]);

const tokenTransferRows = ({ tokenTransfer: t }) =>
  t
    ? [
        { label: "Token Transfer", value: `${t.amount.toLocaleString()} ${t.token}`, testId: "tx-token-transfer" },
        { label: "Token Recipient", value: t.to, icon: User, mono: true, link: `/address/${t.to}` },
      ]
    : [];

const buildDetails = (tx) => {
  const status = statusLabel(tx);
  return [
    { label: "Transaction Hash", value: tx.hash, icon: Hash, mono: true, testId: TX.hash },
    { label: "Status", value: status, icon: STATUS_STYLE[status].icon, status: true, testId: TX.status },
    { label: "Block", value: tx.pending ? "Pending..." : tx.blockNumber?.toLocaleString(), icon: Blocks, link: tx.pending ? null : `/block/${tx.blockNumber}`, testId: TX.blockNumber },
    { label: "From", value: tx.from, icon: User, mono: true, link: `/address/${tx.from}`, testId: TX.from },
    recipientRow(tx),
    ...valueRows(tx),
    ...tokenTransferRows(tx),
    { label: "Gas Price", value: `${formatGwei(tx.gasPrice)} Gwei`, icon: Fuel },
    { label: "Gas Limit", value: tx.gas?.toLocaleString() },
    { label: "Nonce", value: tx.nonce },
  ];
};

const DetailValue = ({ d }) => {
  if (d.status) return <Badge className={STATUS_STYLE[d.value].badge}>{d.value}</Badge>;
  if (d.link) return <Link to={d.link} className="table-link">{d.value}</Link>;
  return <span className="text-white">{d.value}</span>;
};

const DetailRow = ({ d }) => {
  const Icon = d.icon;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
      <div className="flex items-center gap-2 min-w-[160px] text-zinc-500 text-sm">
        {Icon && <Icon size={14} className={d.status ? STATUS_STYLE[d.value].iconClass : ""} />}
        {d.label}:
      </div>
      <div data-testid={d.testId} className={`text-sm break-all ${d.mono ? "font-mono" : ""}`}>
        <DetailValue d={d} />
      </div>
    </div>
  );
};

export default function TxDetail() {
  const { hash } = useParams();
  const { data: tx, isLoading } = useQuery({
    queryKey: ["tx", hash],
    queryFn: () => getTxDetail(hash),
    refetchInterval: (q) => (q.state.data?.pending ? 2000 : false),
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="animate-gold-pulse text-center text-[#D4AF37] py-20 font-display">Loading transaction...</div>
      </div>
    );
  }

  if (!tx) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 text-center text-zinc-400">Transaction not found</div>
    );
  }

  const details = buildDetails(tx);

  return (
    <div data-testid={TX.container} className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <motion.div {...FADE_UP}>
        <div className="flex items-center gap-3 mb-6">
          <Link to="/" className="text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Transaction Details</h1>
          {tx.isDemo && <Badge className="demo-badge">Demo</Badge>}
        </div>

        <Card className="border-[#2A2411] bg-[#0A0A0A]">
          <CardContent className="p-6 space-y-4">
            {details.map((d, i) => (
              <div key={d.label}>
                <DetailRow d={d} />
                {i < details.length - 1 && <Separator className="mt-4 bg-[#2A2411]" />}
              </div>
            ))}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
